import { type HTMLAttributes, type MouseEvent, useCallback } from "react";
import cx from "classnames";
import type { Task } from "src/queries/tasks/tasks.types";
import { ReplayIcon } from "@ui-icons";
import TaskSelectorDefaultRenderOptionTooltip from "./components/TaskSelectorDefaultRenderOptionTooltip/TaskSelectorDefaultRenderOptionTooltip";
import { getTaskTimingDetails } from "./TaskRunSelectorDefaultRenderOption.utils";

export type TaskRunSelectorOptionOnClickType = (
  e: MouseEvent<HTMLLIElement>,
  option: Task
) => void;

export interface TaskSelectorDefaultRenderOptionProps
  extends Omit<HTMLAttributes<HTMLLIElement>, "onClick"> {
  option: Task;
  minStartTime: number;
  totalDuration: number;
  onClick: TaskRunSelectorOptionOnClickType;
}

const TaskRunSelectorDefaultRenderOption = ({
  option,
  minStartTime,
  totalDuration,
  onClick,
  ...props
}: TaskSelectorDefaultRenderOptionProps) => {
  const taskTimingDetails = getTaskTimingDetails(
    option,
    minStartTime,
    totalDuration
  );
  const { startOffset, duration, status } = taskTimingDetails;

  const isRetry = option.retry;

  const onLocalClick = useCallback(
    (e: MouseEvent<HTMLLIElement>) => {
      onClick?.(e, option);
    },
    [onClick, option]
  );

  return (
    <li
      {...props}
      className={cx(
        props.className,
        "relative hover:[--task-bg-opacity:100%] overflow-x-clip! overflow-y-visible!"
      )}
      onClick={onLocalClick}
    >
      <TaskSelectorDefaultRenderOptionTooltip
        option={option}
        isRetry={isRetry}
        {...taskTimingDetails}
      >
        {isRetry && <ReplayIcon color="disabled" />}
        <span
          className={cx("z-10 pointer-events-none", {
            "pl-2": isRetry,
          })}
          style={{
            textShadow: `0 1px 1px var(--mui-palette-background-default)`,
          }}
        >
          {option.task_name}
        </span>
        <div
          className="absolute top-1 bottom-1 border-l-2 border-r-2 rounded-sm opacity-50"
          style={{
            transition: "0.2s",
            background: `color-mix(in srgb, var(--mui-palette-runStatus-${status}) var(--task-bg-opacity, 50%), transparent)`,
            borderColor: `var(--mui-palette-runStatus-${status})`,
            left: `${startOffset}%`,
            width: `${duration}%`,
          }}
        />
      </TaskSelectorDefaultRenderOptionTooltip>
    </li>
  );
};

export default TaskRunSelectorDefaultRenderOption;
